import { CheckCircle2, Circle, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";

// --- Pure helpers (module-level, stable identity) -------------------------

function getStepStatus(idx, currentIndex) {
  if (idx < currentIndex) return "done";
  if (idx === currentIndex) return "active";
  return "pending";
}

const STATUS_ICON = {
  done: CheckCircle2,
  active: Loader2,
  pending: Circle
};

const STATUS_ROW_CLASS = {
  done: "text-muted-foreground",
  active: "bg-secondary",
  pending: "text-muted-foreground/80"
};

const STATUS_BADGE_CLASS = {
  done: "bg-success-soft text-success",
  active: "bg-primary text-primary-foreground",
  pending: "bg-card text-muted-foreground"
};

const STATUS_TITLE_CLASS = {
  done: "text-foreground/80",
  active: "font-medium text-foreground",
  pending: "text-foreground/80"
};

// --- Small subcomponent for one row ---------------------------------------

function ProgressStep({ section, status, completion }) {
  const Icon = STATUS_ICON[status];
  return (
    <li
      className={cn(
        "flex items-start gap-3 rounded-sm px-2 py-2 transition-colors",
        STATUS_ROW_CLASS[status]
      )}
    >
      <span
        className={cn(
          "mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-sm",
          STATUS_BADGE_CLASS[status]
        )}
      >
        <Icon className={cn("h-3.5 w-3.5", status === "active" && "animate-spin")} />
      </span>
      <div className="flex flex-col">
        <span className="font-mono-label text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
          {section.code} · {section.letter}
        </span>
        <span className={cn("text-sm leading-tight", STATUS_TITLE_CLASS[status])}>
          {section.title}
        </span>
        <span className="mt-1 text-[11px] text-muted-foreground">
          {completion.answered}/{completion.required}
        </span>
      </div>
    </li>
  );
}

// --- Main component -------------------------------------------------------

export function IntakeProgress({ sections, currentIndex, completionMap, heading = "Intake progress" }) {
  return (
    <aside className="sticky top-24 hidden lg:block">
      <div className="paper-card p-5">
        <p className="eyebrow">{heading}</p>
        <ol className="mt-4 space-y-1">
          {sections.map((section, idx) => (
            <ProgressStep
              key={section.id}
              section={section}
              status={getStepStatus(idx, currentIndex)}
              completion={completionMap[section.id]}
            />
          ))}
        </ol>
      </div>
      <div className="mt-4 rounded-sm border border-border bg-card/60 p-4">
        <p className="eyebrow">Guarded inputs</p>
        <p className="mt-2 text-xs leading-relaxed text-muted-foreground">
          Do not enter participant names, addresses, identifying incident details, or
          private worker information at any point in this intake.
        </p>
      </div>
    </aside>
  );
}
